/**
 * Preset Manager Web Component
 *
 * Manages persistent display of header, announcement bar, footer presets, and custom fonts across navigation.
 * When a user visits a page with specific presets (e.g., /pages/prograde), the preset IDs and
 * font settings are stored in sessionStorage. On subsequent pages, the stored presets continue
 * to display until the user returns to the homepage, which clears the storage.
 *
 * Font handling is done in preset-manager-init.js which runs before the header-group.
 * This component handles section visibility and initialization.
 */

class PresetManager extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Always initialize footer value pickers once DOM is ready.
    // Footer IDs are section-scoped to avoid duplicates across preset footers,
    // so theme.js's hardcoded ValuePicker("footer-locale-picker") can't handle them.
    this.initFooterValuePickersWhenReady();

    // Use pre-calculated presets from inline script (prevents flash)
    const activePresets = window.ezupActivePresets;

    // If no active presets, nothing to do - Liquid already handled defaults
    if (!activePresets) {
      return;
    }

    // Check if this page already has page_presets (Liquid already handled visibility correctly)
    const pagePresets = window.ezupPresetManager?.pagePresets;
    if (pagePresets && (pagePresets.header_id || pagePresets.annoucement_bar_id || pagePresets.footer_id || pagePresets.sections_id)) {
      // Liquid already rendered the correct sections, no JS override needed
      return;
    }

    // This is a page without page_presets metafield, but we have stored presets
    // We need to override the Liquid defaults and show the stored preset sections

    // Apply header preset
    if (activePresets.header_id) {
      this.applyPresetToSections('header', activePresets.header_id);
    }

    // Apply announcement bar preset
    if (activePresets.annoucement_bar_id) {
      this.applyPresetToSections('announcement-bar', activePresets.annoucement_bar_id);
    }

    // Apply footer preset
    if (activePresets.footer_id) {
      this.applyPresetToSections('footer', activePresets.footer_id);
    }

    // Apply sections preset (slideshow, custom HTML, custom Liquid, contact-section)
    // Pass keepUniversal=true so sections with preset_id 0 or empty always show
    if (activePresets.sections_id) {
      this.applyPresetToSections('slideshow', activePresets.sections_id, true);
      this.applyPresetToSections('html', activePresets.sections_id, true);
      this.applyPresetToSections('liquid', activePresets.sections_id, true);
      this.applyPresetToSections('contact-section', activePresets.sections_id, true);
    }
  }

  /**
   * Show/hide sections based on preset ID
   * @param {string} sectionType - The section type to apply presets to
   * @param {number|string} presetId - The preset ID to show
   * @param {boolean} keepUniversal - If true, sections with empty preset_id always show (for slideshow/html/liquid)
   */
  applyPresetToSections(sectionType, presetId, keepUniversal = false) {
    // Find all sections of this type
    const sections = document.querySelectorAll(`[data-section-type="${sectionType}"]`);
    let visibleSection = null;

    sections.forEach(section => {
      const sectionPresetId = section.getAttribute('data-preset-id');
      // Universal = empty preset ID (shows on all pages)
      // Default only = preset ID "0" (shows only when no preset active)
      const isUniversal = !sectionPresetId || sectionPresetId === '';
      const isDefaultOnly = sectionPresetId === '0';

      // Determine if this section should be shown
      let shouldShow = false;

      if (presetId === 0 || presetId === '0' || !presetId) {
        // No active preset - show default sections (0) and universal (empty)
        shouldShow = isDefaultOnly || isUniversal;
      } else {
        // Active preset - show matching sections and universal (empty) if keepUniversal
        shouldShow = sectionPresetId === String(presetId) || (keepUniversal && isUniversal);
      }

      // Apply visibility - clear inline styles and remove hidden attribute
      if (shouldShow) {
        section.style.removeProperty('display');
        section.removeAttribute('hidden');
        section.removeAttribute('style'); // Clear all inline styles set by Liquid
        visibleSection = section;
      } else {
        section.style.display = 'none';
        section.setAttribute('hidden', '');
      }
    });

    // Update CSS variables and initialize components for the visible section
    if (visibleSection) {
      this.initializeSection(sectionType, visibleSection);
    }
  }

  /**
   * Initialize section components and CSS variables when showing via JS
   */
  initializeSection(sectionType, section) {
    // Use requestAnimationFrame to ensure the element is visible before measuring
    requestAnimationFrame(() => {
      const sectionId = section.getAttribute('data-section-id');
      const sectionWrapper = document.getElementById(`shopify-section-${sectionId}`);

      if (sectionType === 'header' && sectionWrapper) {
        const height = sectionWrapper.clientHeight;
        if (height > 0) {
          document.documentElement.style.setProperty('--header-height', height + 'px');
        }
      }

      if (sectionType === 'announcement-bar' && sectionWrapper) {
        const height = sectionWrapper.clientHeight;
        if (height > 0) {
          document.documentElement.style.setProperty('--announcement-bar-height', height + 'px');
        }

        // Update newsletter button width if present
        const newsletterBtn = section.querySelector('.announcement-bar__button');
        if (newsletterBtn) {
          document.documentElement.style.setProperty('--announcement-bar-button-width', newsletterBtn.clientWidth + 'px');
        }

        // Initialize Flickity carousel if present and not already initialized
        this.initializeCarousel(section);
      }
    });
  }

  /**
   * Schedule footer value picker initialization after DOM is ready
   * (footer-group renders after <preset-manager> in theme.liquid)
   */
  initFooterValuePickersWhenReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initFooterValuePickers());
    } else {
      this.initFooterValuePickers();
    }
  }

  /**
   * Initialize value pickers for all footer sections.
   * Each footer has section-scoped IDs (e.g. footer-locale-picker-<section_id>)
   * to avoid duplicate ID conflicts when multiple preset footers exist in the DOM.
   */
  initFooterValuePickers() {
    const footers = document.querySelectorAll('[data-section-type="footer"]');
    footers.forEach(footer => this.setupFooterValuePickers(footer));

    // Single global listener for outside-click-to-close across all footers
    document.addEventListener('click', (e) => {
      footers.forEach(footer => {
        if (footer.hasAttribute('hidden') || footer.style.display === 'none') return;
        footer.querySelectorAll('.value-picker[aria-hidden="false"]').forEach(picker => {
          const opener = footer.querySelector(`[data-action="open-value-picker"][aria-controls="${picker.id}"]`);
          if (!picker.contains(e.target) && (!opener || !opener.contains(e.target))) {
            picker.setAttribute('aria-hidden', 'true');
            if (opener) opener.setAttribute('aria-expanded', 'false');
          }
        });
      });
    });
  }

  /**
   * Attach open/close handlers to value pickers within a single footer section
   */
  setupFooterValuePickers(footer) {
    footer.querySelectorAll('.footer__localization-form [data-action="open-value-picker"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const pickerId = btn.getAttribute('aria-controls');
        const picker = document.getElementById(pickerId);
        if (!picker) return;
        const isOpen = picker.getAttribute('aria-hidden') === 'false';
        btn.setAttribute('aria-expanded', String(!isOpen));
        picker.setAttribute('aria-hidden', String(isOpen));
      });
    });

    footer.querySelectorAll('.footer__localization-form [data-action="close-value-picker"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const pickerId = btn.getAttribute('aria-controls');
        const picker = document.getElementById(pickerId);
        if (!picker) return;
        picker.setAttribute('aria-hidden', 'true');
        const opener = footer.querySelector(`[data-action="open-value-picker"][aria-controls="${pickerId}"]`);
        if (opener) opener.setAttribute('aria-expanded', 'false');
      });
    });
  }

  /**
   * Initialize Flickity carousel for announcement bar
   */
  initializeCarousel(section) {
    const carousel = section.querySelector('.announcement-bar__carousel');
    if (!carousel) return;

    // Check if Flickity is already initialized
    if (carousel.classList.contains('flickity-enabled')) return;

    // Wait for Flickity to be available (it's loaded with theme.js)
    const initFlickity = () => {
      if (typeof Flickity !== 'undefined') {
        new Flickity(carousel, {
          autoPlay: true,
          pauseAutoPlayOnHover: true,
          resize: false,
          fullscreen: true,
          contain: true,
          cellAlign: 'center',
          wrapAround: true,
          prevNextButtons: false,
          pageDots: false,
          draggable: true,
          groupCells: false
        });
      } else {
        // Flickity not loaded yet, try again
        setTimeout(initFlickity, 50);
      }
    };

    initFlickity();
  }
}

// Register the custom element
if (!customElements.get('preset-manager')) {
  customElements.define('preset-manager', PresetManager);
}
